import { Form, FormInstance } from "antd"
import { RuleObject } from "antd/es/form"
import { useTranslation } from "@hooks/useTranslation"

export function useForm<T>() {
  return Form.useForm<T>()
}

export function useFormInstance<T>() {
  return Form.useFormInstance<T>()
}

export function useWatchFormField<
  TForm,
  T1 extends keyof TForm = keyof TForm,
  T2 extends keyof TForm[T1] = keyof TForm[T1],
  T3 extends keyof TForm[T1][T2] = keyof TForm[T1][T2],
  T4 extends keyof TForm[T1][T2][T3] = keyof TForm[T1][T2][T3],
>(dependencies: [T1, T2, T3, T4], form?: FormInstance<TForm>): TForm[T1][T2][T3][T4]

export function useWatchFormField<
  TForm,
  T1 extends keyof TForm = keyof TForm,
  T2 extends keyof TForm[T1] = keyof TForm[T1],
  T3 extends keyof TForm[T1][T2] = keyof TForm[T1][T2],
>(dependencies: [T1, T2, T3], form?: FormInstance<TForm>): TForm[T1][T2][T3]

export function useWatchFormField<
  TForm,
  T1 extends keyof TForm = keyof TForm,
  T2 extends keyof TForm[T1] = keyof TForm[T1],
>(dependencies: [T1, T2], form?: FormInstance<TForm>): TForm[T1][T2]

export function useWatchFormField<TForm, T1 extends keyof TForm = keyof TForm>(
  dependencies: T1 | [T1],
  form?: FormInstance<TForm>
): TForm[T1]

export function useWatchFormField<TForm>(dependencies: [], form?: FormInstance<TForm>): TForm

export function useWatchFormField<TForm, TSelected>(
  selector: (values: TForm) => TSelected,
  form?: FormInstance<TForm>
): TSelected

export function useWatchFormField<FormData>(...arg: Parameters<typeof Form.useWatch<FormInstance<FormData>>>) {
  const [name, form] = arg

  let refForm = form
  const formInstance = useFormInstance<FormData>()

  if (!form) {
    refForm = formInstance
  }

  return Form.useWatch<unknown, FormInstance<FormData>>(name, refForm)
}

export function useFormRules() {
  const t = useTranslation("common.validations.errors")

  return {
    required: (params: Omit<RuleObject, "required"> = {}) => ({
      required: true,
      message: t("required"),
      ...params,
    }),
    maxLength: (params: Omit<RuleObject, "transform"> & { max: number }) => ({
      validator: (_: any, raw: unknown) => {
        const value = String(raw)

        if (value && value.length > params.max) {
          return Promise.reject(t("maxLength", { max: params.max, current: value.length }))
        }

        return Promise.resolve()
      },
    }),
    min: (params: Omit<RuleObject, "transform"> & { min: number }) => ({
      transform: params.type === "number" ? (val: any) => (val ? Number(val) : val) : undefined,
      message: t("min"),
      ...params,
    }),
    max: (params: Omit<RuleObject, "transform"> & { max: number }) => ({
      transform: params.type === "number" ? (val: any) => (val ? Number(val) : val) : undefined,
      message: t("max"),
      ...params,
    }),
    emailAtSign: (params: Omit<RuleObject, "pattern"> = {}) => ({
      pattern: /^[^@]+@[\w-]+(\.[\w-]+)+$/,
      message: t("email.missingAtSign"),
      ...params,
    }),
    emailCharacter: (params: Omit<RuleObject, "pattern"> = {}) => ({
      pattern: /^[a-zA-Z0-9._@+-]+$/,
      message: t("email.isValidCharacters"),
      ...params,
    }),
    password: (params: Omit<RuleObject, "pattern"> & { min?: number; max?: number } = { min: 8, max: 32 }) => ({
      pattern: new RegExp(`^(?=.*[a-zA-Z])(?=.*\\d)[\x21-\x7E]{${params?.min || 8},${params?.max || 32}}$`, "i"),
      message: t("password"),
      ...params,
    }),
    text: (params: Omit<RuleObject, "pattern"> = {}) => ({
      pattern: /^[A-Za-z0-9._]+$/,
      message: t("text"),
      ...params,
    }),
    katakanaField: (params: Omit<RuleObject, "pattern"> = {}) => ({
      pattern: /^[\u30A0-\u30FF]+$/,
      message: t("katakanaField"),
      ...params,
    }),
  }
}
